import React, { useEffect, useRef, useState } from 'react';
import { View, Text, TextInput, StyleSheet, Pressable, Platform } from 'react-native';
import { Check } from 'lucide-react-native';
import { useTheme, TYPOGRAPHY, SPACING, BORDER_RADIUS, ON_COLOR } from '../../theme';
import { AdaptiveSheet, Button, ErrorBanner, Input, TextLink } from '../common';
import { GoogleSignInButton } from './GoogleSignInButton';
import { useAuthStore } from '../../store/authStore';
import { USER_ROLE_META, type UserRole } from '../../types';
import { MIN_PASSWORD_LENGTH } from '../../constants/auth';
// Same 9-role chip set as sign-up. Saving an account is the natural moment
// to ask "what other roles do you play?" (client plan, timing tab) — roles
// here are self-declared; agent verification still happens server-side.
import { ROLE_ORDER } from '../../constants/roleIcons';
import { useUIStore } from '../../store/uiStore';

interface UpgradeAccountBodyProps {
  visible: boolean;
  onClose: () => void;
}

/**
 * Convert the current anonymous Fire Scout to a real email/password
 * account via Firebase `linkWithCredential`. The user's uid stays the
 * same — every post, comment, and claim they created stays attributed.
 *
 * Split from its sheet wrapper (panel-system.md Phase 3B) so it can also
 * render as a PanelStack drill-in inside Tour REDI (HowReDIWorksSheet)
 * with no second sheet stacked on top. The global `upgradeAccount` modal
 * (UserMenuDropdown, and every other call site) keeps using the
 * `UpgradeAccountSheet` wrapper below, unchanged.
 *
 * No AdaptiveSheet/ScrollView here — the host provides the single
 * scrollable region, same convention as DefensePlanAssignForm.web's
 * AssignRoot. This component uses no provider-dependent hook (no
 * useConfirm), so it needs nothing passed down for the tour's gorhom-portal
 * gotcha.
 */
export function UpgradeAccountBody({ visible, onClose }: UpgradeAccountBodyProps) {
  const { colors } = useTheme();
  const upgradeAccount = useAuthStore((s) => s.upgradeAccount);
  const upgradeAccountWithGoogle = useAuthStore((s) => s.upgradeAccountWithGoogle);
  const signInWithPendingGoogleCredential = useAuthStore(
    (s) => s.signInWithPendingGoogleCredential
  );
  const updateRoles = useAuthStore((s) => s.updateRoles);
  const user = useAuthStore((s) => s.user);
  const isLoading = useAuthStore((s) => s.isLoading);
  const error = useAuthStore((s) => s.error);
  const clearError = useAuthStore((s) => s.clearError);

  const lastNameRef = useRef<TextInput>(null);
  const emailRef = useRef<TextInput>(null);
  const passwordRef = useRef<TextInput>(null);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // A scout who came in on an audience intro page keeps that role through
  // the upgrade, the same as the sign-up form does.
  const pendingAudience = useUIStore((s) => s.pendingAudience);
  const clearPendingAudience = useUIStore((s) => s.clearPendingAudience);
  const [selectedRoles, setSelectedRoles] = useState<Set<UserRole>>(() => {
    const base: UserRole[] = user?.roles?.length ? (user.roles as UserRole[]) : ['fire-scout'];
    return new Set<UserRole>(pendingAudience ? [...base, pendingAudience] : base);
  });
  const [googleBusy, setGoogleBusy] = useState(false);
  const [showExistingAccount, setShowExistingAccount] = useState(false);

  // Open-time work only — present/dismiss + the tab-bar hold live in
  // AdaptiveSheet now.
  useEffect(() => {
    if (visible) {
      clearError();
      setShowExistingAccount(false);
      setSelectedRoles(
        new Set<UserRole>(user?.roles?.length ? (user.roles as UserRole[]) : ['fire-scout'])
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-seed roles only on open
  }, [visible, clearError]);

  const toggleRole = (r: UserRole) => {
    if (r === 'fire-scout') return;
    setSelectedRoles((prev) => {
      const next = new Set(prev);
      if (next.has(r)) next.delete(r);
      else next.add(r);
      return next;
    });
  };

  // Persist any roles picked alongside the upgrade. Best-effort: a failure
  // here shouldn't undo a successful account upgrade, and the user can
  // re-pick roles from the profile sheet anytime. Shared by both the
  // email and Google upgrade paths.
  const saveSelectedRolesIfAny = async (userId: string | undefined) => {
    clearPendingAudience();
    if (!userId || selectedRoles.size <= 1) return;
    const roles: UserRole[] = [
      'fire-scout',
      ...Array.from(selectedRoles).filter((r) => r !== 'fire-scout'),
    ];
    try {
      await updateRoles(roles);
    } catch {
      // Non-fatal: upgrade itself already succeeded.
    }
  };

  const handleSubmit = async () => {
    if (!isValid) return;
    try {
      const userId = user?.id;
      await upgradeAccount({
        email: email.trim(),
        password,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      });
      await saveSelectedRolesIfAny(userId);
      // Success — the user doc now has isAnonymous: false. The auth
      // listener will repopulate the user; we just close the sheet.
      onClose();
    } catch {
      // Error is surfaced via authStore.error, which we render below.
    }
  };

  const handleGoogleUpgrade = async () => {
    setGoogleBusy(true);
    try {
      const userId = user?.id;
      const status = await upgradeAccountWithGoogle();
      if (status === 'upgraded') {
        await saveSelectedRolesIfAny(userId);
        onClose();
      } else if (status === 'account-exists') {
        setShowExistingAccount(true);
      }
      // 'cancelled' — the user dismissed the popup; no-op.
    } catch {
      // Error is surfaced via authStore.error, which we render below.
    } finally {
      setGoogleBusy(false);
    }
  };

  const handleSignInToExisting = async () => {
    try {
      await signInWithPendingGoogleCredential();
      onClose();
    } catch {
      // Error is surfaced via authStore.error, which we render below.
    }
  };

  const isValid =
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    email.trim().length > 0 &&
    password.length >= MIN_PASSWORD_LENGTH;

  return (
    <View style={styles.content}>
      <Text style={[styles.title, { color: colors.text }]}>Save your Fire Scout account</Text>
      <Text style={[styles.blurb, { color: colors.textSecondary }]}>
        Add an email and password so your posts and progress stay with you across devices and
        browser sessions. Your existing observations will keep their original attribution.
      </Text>

      {Platform.OS === 'web' &&
        (showExistingAccount ? (
          <View
            style={[
              styles.existingAccountCard,
              { backgroundColor: colors.surface, borderColor: colors.border },
            ]}>
            <Text style={[styles.existingAccountText, { color: colors.text }]}>
              That Google account already has a REDI account. You can sign in to it instead. Posts
              you made as a guest will stay on the guest profile.
            </Text>
            <Button
              title="Sign in to that account"
              variant="secondary"
              onPress={handleSignInToExisting}
              fullWidth
              testID="upgrade-google-existing-signin"
            />
            <TextLink
              label="Never mind"
              onPress={() => setShowExistingAccount(false)}
              style={styles.existingAccountDismiss}
            />
          </View>
        ) : (
          <>
            <GoogleSignInButton
              testID="upgrade-google"
              loading={googleBusy}
              onPress={handleGoogleUpgrade}
            />
            <View style={styles.dividerRow}>
              <View style={[styles.dividerLine, { backgroundColor: colors.divider }]} />
              <Text style={[styles.dividerText, { color: colors.textTertiary }]}>
                or save with email
              </Text>
              <View style={[styles.dividerLine, { backgroundColor: colors.divider }]} />
            </View>
          </>
        ))}

      <View style={styles.nameRow}>
        <View style={styles.nameField}>
          <Input
            label="First name"
            value={firstName}
            onChangeText={setFirstName}
            placeholder="First"
            autoCapitalize="words"
            returnKeyType="next"
            onSubmitEditing={() => lastNameRef.current?.focus()}
            blurOnSubmit={false}
          />
        </View>
        <View style={styles.nameField}>
          <Input
            ref={lastNameRef}
            label="Last name"
            value={lastName}
            onChangeText={setLastName}
            placeholder="Last"
            autoCapitalize="words"
            returnKeyType="next"
            onSubmitEditing={() => emailRef.current?.focus()}
            blurOnSubmit={false}
          />
        </View>
      </View>

      <Input
        ref={emailRef}
        label="Email"
        value={email}
        onChangeText={setEmail}
        placeholder="Enter your email"
        keyboardType="email-address"
        autoCapitalize="none"
        autoCorrect={false}
        returnKeyType="next"
        onSubmitEditing={() => passwordRef.current?.focus()}
        blurOnSubmit={false}
        testID="upgrade-email"
      />

      <Input
        ref={passwordRef}
        label="Password"
        value={password}
        onChangeText={setPassword}
        placeholder="Create a password"
        secureTextEntry
        returnKeyType="done"
        onSubmitEditing={handleSubmit}
        hint={`At least ${MIN_PASSWORD_LENGTH} characters`}
        testID="upgrade-password"
      />

      <View style={styles.roleSection}>
        <Text style={[styles.roleLabel, { color: colors.textSecondary }]}>
          You&apos;re a Fire Scout. What other roles do you play?
        </Text>
        <View style={styles.roleRow}>
          {ROLE_ORDER.map((r) => {
            const selected = selectedRoles.has(r);
            const locked = r === 'fire-scout';
            return (
              <Pressable
                key={r}
                onPress={() => toggleRole(r)}
                accessibilityRole="checkbox"
                accessibilityState={{ checked: selected, disabled: locked }}
                style={[
                  styles.rolePill,
                  {
                    backgroundColor: selected ? colors.primary : 'transparent',
                    borderColor: selected ? colors.primary : colors.border,
                  },
                ]}>
                {selected && <Check size={13} color={ON_COLOR.text} />}
                <Text
                  style={[styles.rolePillText, { color: selected ? ON_COLOR.text : colors.text }]}>
                  {USER_ROLE_META[r].label}
                </Text>
              </Pressable>
            );
          })}
        </View>
        <Text style={[styles.roleHint, { color: colors.textTertiary }]}>
          Optional. You can change these anytime from your profile.
        </Text>
      </View>

      <ErrorBanner message={error} style={styles.errorSpacing} />

      <Button
        title="Save account"
        onPress={handleSubmit}
        loading={isLoading}
        disabled={!isValid}
        fullWidth
        size="lg"
        testID="upgrade-submit"
      />
      {/* Scroll sentinel: Maestro flows assert this is reachable. */}
      <View testID="upgrade-sheet-end" style={styles.scrollSentinel} />
    </View>
  );
}

interface UpgradeAccountSheetProps {
  visible: boolean;
  onClose: () => void;
}

/**
 * Sheet wrapper around `UpgradeAccountBody` for every global call site
 * (UserMenuDropdown's `upgradeAccount` uiStore sheet, app/structure/[id],
 * AddPropertyForm) — stays a blocking modal everywhere outside the tour,
 * per panel-system.md's "these stay dialogs" list. Unchanged behavior and
 * testIDs from before the Phase 3B split.
 */
export function UpgradeAccountSheet({ visible, onClose }: UpgradeAccountSheetProps) {
  return (
    <AdaptiveSheet
      isOpen={visible}
      onRequestClose={onClose}
      onDismiss={onClose}
      keyboardBehavior="interactive"
      keyboardBlurBehavior="restore"
      androidKeyboardInputMode="adjustResize">
      <AdaptiveSheet.ScrollView testID="upgrade-sheet" keyboardShouldPersistTaps="handled">
        <UpgradeAccountBody visible={visible} onClose={onClose} />
      </AdaptiveSheet.ScrollView>
    </AdaptiveSheet>
  );
}

const styles = StyleSheet.create({
  content: {
    padding: SPACING.lg,
    paddingBottom: SPACING.xxl,
  },
  title: {
    ...TYPOGRAPHY.h4,
    marginBottom: SPACING.sm,
  },
  blurb: {
    ...TYPOGRAPHY.bodySmall,
    lineHeight: 20,
    marginBottom: SPACING.lg,
  },
  dividerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: SPACING.lg,
  },
  dividerLine: {
    flex: 1,
    height: StyleSheet.hairlineWidth,
  },
  dividerText: {
    ...TYPOGRAPHY.bodySmall,
    marginHorizontal: SPACING.md,
  },
  existingAccountCard: {
    borderWidth: 1,
    borderRadius: BORDER_RADIUS.lg,
    padding: SPACING.md,
    marginBottom: SPACING.lg,
  },
  existingAccountText: {
    ...TYPOGRAPHY.bodySmall,
    marginBottom: SPACING.md,
  },
  existingAccountDismiss: {
    alignSelf: 'center',
    marginTop: SPACING.sm,
  },
  nameRow: {
    flexDirection: 'row',
    gap: SPACING.smd,
  },
  nameField: {
    flex: 1,
  },
  roleSection: {
    marginBottom: SPACING.md,
  },
  roleLabel: {
    ...TYPOGRAPHY.label,
    marginBottom: SPACING.sm,
  },
  roleRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: SPACING.sm,
  },
  rolePill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.xs,
    paddingVertical: SPACING.xs,
    paddingHorizontal: SPACING.smd,
    borderRadius: BORDER_RADIUS.full,
    borderWidth: 1,
  },
  rolePillText: {
    ...TYPOGRAPHY.label,
  },
  roleHint: {
    ...TYPOGRAPHY.caption,
    marginTop: SPACING.sm,
  },
  errorSpacing: {
    marginBottom: SPACING.md,
  },
  scrollSentinel: {
    height: 1,
  },
});
